import { AxiosRequestConfig } from "axios";
import useApiGet, { UseApiGetOptions } from "hooks/api/useApiGet";
import usePagination from "features/Pagination/hooks/usePagination";
import { useEffect, useState } from "react";
import PaginatedResponse from "types/PaginatedResponse";

const DEFAULT_PAGE_SIZE = 9999;

export interface PaginationOptions extends UseApiGetOptions {
  pageSize?: number;
  page?: number;
}

export default function useApiGetWithPagination<T>(
  baseEndpoint: string,
  { options }: { options?: PaginationOptions & AxiosRequestConfig } = {},
) {
  const [mutations, setMutations] = useState<Array<() => void>>([]);
  const _options = { pageSize: DEFAULT_PAGE_SIZE, ...options };
  const {
    page,
    resultsByPage,
    setHasNextPage,
    setResultsByPage,
    setTotalPages,
    totalPages,
    ...restPagination
  } = usePagination<T>();

  const { result, ...rest } = useApiGet<PaginatedResponse<T>>(
    baseEndpoint
      ? `${baseEndpoint}&size=${_options.pageSize}&page=${_options.page != undefined ? _options.page : page}`
      : "",
    {
      axiosOptions: options?.axiosOptions,
      swrOptions: options?.swrOptions,
    },
  );

  useEffect(() => {
    if (result) {
      setResultsByPage(
        page === 0
          ? { 0: result.dataList }
          : { ...resultsByPage, [result.number]: result.dataList },
      );
      setHasNextPage(page + 1 < result.totalPages);
      setTotalPages(result.totalPages);
      setMutations([...mutations, rest.mutate]);
    }
  }, [result]);

  function mutate() {
    mutations.forEach((mutation) => mutation());
    restPagination.reset();
  }

  return {
    ...rest,
    ...restPagination,
    mutate,
    page,
    resultsByPage,
    setHasNextPage,
    setTotalPages,
    totalPages,
    ...result,
  };
}
